import React, { useEffect, useRef, useState } from 'react';
import { Spin, Table, Input, Button } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getUnsettledPaymentsAction,
  updateUnsettledPaymentsAction,
  updateLoanPaymentSplitAction,
} from '../../Loan/store/loanActions';

interface LoanPayment {
  loanNumber: string | undefined;
  cashier: string;
  transactionDate: string;
  description: string;
  notes?: string;
  branch: string;
  paymentType: string;
  _id: string;
  interestAmount: number;
  principal: number | undefined;
  onlinePrincipal?: number;
  offlinePrincipal?: number;
  onlineInterest?: number;
  offlineInterest?: number;
  OtherTransaction?: {
    Amount: number;
    Description: string;
  };
  paymentMethod?: string;
  customerId?: string;
  reducingInterest: number;
}

export default function LoanPaymentsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const { unsettledPayments }: any = useSelector<any>((state) => state?.loan);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [onlinePrincipalValue, setOnlinePrincipalValue] = useState<string>('');
  const [offlinePrincipalValue, setOfflinePrincipalValue] = useState<string>('');
  const [onlineInterestValue, setOnlineInterestValue] = useState<string>('');
  const [offlineInterestValue, setOfflineInterestValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const processingRef = useRef(false);

  const clearEdit = () => {
    setEditingId(null);
    setOnlinePrincipalValue('');
    setOfflinePrincipalValue('');
    setOnlineInterestValue('');
    setOfflineInterestValue('');
  };

  const getLoanPayments = async (): Promise<void> => {
    await getUnsettledPaymentsAction(dispatch, {});
    processingRef.current = false;
    setSaving(false);
    clearEdit();
  };

  useEffect(() => {
    void getLoanPayments();
  }, []);

  const handleEdit = (record: LoanPayment) => {
    setEditingId(record._id);
    setOnlinePrincipalValue(String(record.onlinePrincipal ?? ''));
    setOfflinePrincipalValue(String(record.offlinePrincipal ?? ''));
    setOnlineInterestValue(String(record.onlineInterest ?? ''));
    setOfflineInterestValue(String(record.offlineInterest ?? ''));
  };

  // Both splits must reconcile to the totals the payment was raised with
  const isPrincipalSplitValid = (record: LoanPayment) =>
    Math.abs(
      Number(onlinePrincipalValue) +
        Number(offlinePrincipalValue) -
        Number(record.principal ?? 0)
    ) <= 0.009;

  const isInterestSplitValid = (record: LoanPayment) =>
    Math.abs(
      Number(onlineInterestValue) +
        Number(offlineInterestValue) -
        Number(record.interestAmount ?? 0)
    ) <= 0.009;

  const handleSave = async (record: LoanPayment) => {
    if (!isPrincipalSplitValid(record)) {
      alert('Online Principal + Offline Principal must match Principal amount');
      return;
    }
    if (!isInterestSplitValid(record)) {
      alert('Online Interest + Offline Interest must match Interest amount');
      return;
    }

    setSaving(true);
    await updateLoanPaymentSplitAction(dispatch, {
      id: record._id,
      onlinePrincipal: Number(onlinePrincipalValue),
      offlinePrincipal: Number(offlinePrincipalValue),
      onlineInterest: Number(onlineInterestValue),
      offlineInterest: Number(offlineInterestValue),
    });
    await getLoanPayments();
  };

  const handleUpdate = async (loanPayment: LoanPayment, status: string) => {
    try {
      await updateUnsettledPaymentsAction(dispatch, {
        id: loanPayment._id,
        status,
      });
      void getLoanPayments();
    } catch (error) {
      console.error('Error updating payment:', error);
      processingRef.current = false;
    } finally {
      processingRef.current = false;
    }
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: LoanPayment, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Loan Number',
      dataIndex: 'loanNumber',
      key: 'loanNumber',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.loanNumber}</p>
      ),
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.principal}</p>
      ),
    },
    {
      title: 'Online Principal',
      dataIndex: 'onlinePrincipal',
      key: 'onlinePrincipal',
      render: (_: any, record: LoanPayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlinePrincipalValue}
            onChange={(e) => {
              setOnlinePrincipalValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlinePrincipal}</p>
        ),
    },
    {
      title: 'Offline Principal',
      dataIndex: 'offlinePrincipal',
      key: 'offlinePrincipal',
      render: (_: any, record: LoanPayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlinePrincipalValue}
            onChange={(e) => {
              setOfflinePrincipalValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlinePrincipal}</p>
        ),
    },
    {
      title: 'Interest',
      dataIndex: 'interest',
      key: 'interest',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.interestAmount}</p>
      ),
    },
    {
      title: 'Online Interest',
      dataIndex: 'onlineInterest',
      key: 'onlineInterest',
      render: (_: any, record: LoanPayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlineInterestValue}
            onChange={(e) => {
              setOnlineInterestValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlineInterest}</p>
        ),
    },
    {
      title: 'Offline Interest',
      dataIndex: 'offlineInterest',
      key: 'offlineInterest',
      render: (_: any, record: LoanPayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlineInterestValue}
            onChange={(e) => {
              setOfflineInterestValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlineInterest}</p>
        ),
    },
    {
      title: 'Edit',
      dataIndex: 'edit',
      key: 'edit',
      width: 120,
      render: (_: any, record: LoanPayment) =>
        editingId === record._id ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              icon={<SaveOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              loading={saving}
              disabled={
                !isPrincipalSplitValid(record) || !isInterestSplitValid(record)
              }
              onClick={() => {
                void handleSave(record);
              }}
            ></Button>
            <Button
              icon={<CloseOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              disabled={saving}
              onClick={clearEdit}
            ></Button>
          </div>
        ) : (
          <Button
            icon={<EditOutlined style={{ color: '#000' }} />}
            size="small"
            type="link"
            onClick={() => {
              handleEdit(record);
            }}
          ></Button>
        ),
    },
    {
      title: 'Accept',
      dataIndex: 'actions',
      width: 200,
      render: (_: any, record: LoanPayment, index: number) => {
        const settleDisabled = processingRef.current || editingId === record._id;
        return (
          <button
            className="search__button"
            style={{
              background: !settleDisabled ? '#27ae60' : '#b2b2b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 24px',
              fontWeight: 700,
              cursor: !settleDisabled ? 'pointer' : 'not-allowed',
              opacity: !settleDisabled ? 1 : 0.7,
            }}
            disabled={settleDisabled}
            onClick={() => {
              if (!settleDisabled) {
                processingRef.current = true;
                void handleUpdate(record, 'cash_received');
              }
            }}
          >
            Settle Payment
          </button>
        );
      },
    },
    {
      title: 'Reject',
      dataIndex: 'actions2',
      width: 200,
      render: (_: any, record: LoanPayment, index: number) => (
        <button
          className="search__button"
          style={{
            background: !processingRef.current ? '#e74c3c' : '#b2b2b2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 24px',
            fontWeight: 700,
            cursor: !processingRef.current ? 'pointer' : 'not-allowed',
            opacity: !processingRef.current ? 1 : 0.7,
          }}
          disabled={processingRef.current}
          onClick={() => {
            if (!processingRef.current) {
              processingRef.current = true;
              void handleUpdate(record, 'rejected');
            }
          }}
        >
          Reject Payment
        </button>
      ),
    },
  ];

  return (
    <div className="users__table__page">
      {!(unsettledPayments.length > 0) && <Spin spinning={false} fullscreen />}
      <div style={{ width: '100%' }}>
        <Table dataSource={unsettledPayments} columns={columns} />
      </div>
    </div>
  );
}
